import { readValue, asArray, buildStorefrontUrl, formatPrice, validationMessage } from "../utils/storefrontApi";
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CategoryOutlined,
  Inventory2Outlined,
  LaunchOutlined,
  ReplayOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import axiosInstanceSearch from "../axiosInstanceSearch";

const DEBOUNCE_MILLISECONDS = 120;
const MINIMUM_CHARACTERS = 2;
const REQUEST_TIMEOUT_MILLISECONDS = 5000;
const MAXIMUM_QUERY_LENGTH = 128;
const SUGGESTION_LISTBOX_ID = "experimental-typeahead-suggestions";

const normalizeTypeaheadInput = (value = "") => value
  .normalize("NFD")
  .replace(/\p{Mark}/gu, "")
  .toLocaleLowerCase("en")
  .replace(/[^\p{Letter}\p{Number}]+/gu, " ")
  .trim();

const countNormalizedCharacters = (value) => (value.match(/[\p{Letter}\p{Number}]/gu) || []).length;
const searchableCharacterCount = (value) => countNormalizedCharacters(normalizeTypeaheadInput(value));

const buildTypeaheadUrl = (baseUrl) =>
  buildStorefrontUrl(baseUrl, "typeahead");

const getErrorMessage = (error) => {
  const data = error?.response?.data;
  const validation = validationMessage(data);
  if (validation) return validation;
  const statusMessage = {
    401: "This Search API requires authorization.",
    413: "The request is larger than the Search API limit.",
    429: "This tenant is being rate limited. Wait briefly and retry.",
    503: "The Search API timed out or a dependency is unavailable.",
  }[error?.response?.status];
  const messageKey = data?.messageKey || data?.extensions?.messageKey;
  const traceId = error?.response?.headers?.["trace-id"];
  const suffix = [messageKey, traceId ? `Trace ${traceId}` : null].filter(Boolean).join(" · ");
  return `${data?.title || statusMessage || "Suggestions are temporarily unavailable."}${suffix ? ` · ${suffix}` : ""}`;
};

const matchKindColor = (matchKind = "") => {
  if (matchKind === "exact") return "success";
  if (matchKind === "complete") return "warning";
  if (matchKind.startsWith("synonym-")) return "secondary";
  return "default";
};


const SearchQueryInput = ({
  baseUrl,
  selectedTenant,
  onSubmitQuery,
  query: controlledQuery,
  setQuery: setControlledQuery,
  enabled = false,
  inputSx,
}) => {
  const [localQuery, setLocalQuery] = useState("");
  const query = controlledQuery ?? localQuery;
  const setQuery = setControlledQuery ?? setLocalQuery;
  const [response, setResponse] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [retryKey, setRetryKey] = useState(0);
  const inputRef = useRef(null);
  const generationRef = useRef(0);
  const activeRequestRef = useRef(null);

  const normalizedQuery = normalizeTypeaheadInput(query);
  const eligible = countNormalizedCharacters(normalizedQuery) >= MINIMUM_CHARACTERS;
  const suggestions = !enabled || status === "idle" || status === "error" ? null : response;
  const products = asArray(readValue(suggestions, "products", "Products"));
  const categories = asArray(readValue(suggestions, "categories", "Categories"));
  const suggestionCount = categories.length + products.length;
  const popupOpen = enabled && (status === "loading" || status === "error" || status === "empty" || suggestionCount > 0);
  const activeOptionId = activeIndex >= 0 ? `typeahead-option-${generationRef.current}-${activeIndex}` : undefined;

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    const previous = activeRequestRef.current;
    if (previous) {
      previous.controller.abort();
      clearTimeout(previous.timeoutId);
      activeRequestRef.current = null;
    }
    setResponse(null);
    setError("");
    setActiveIndex(-1);

    if (!enabled || !query.trim() || query.length > MAXIMUM_QUERY_LENGTH) {
      setStatus("idle");
      return undefined;
    }
    if (!eligible) {
      setStatus("suppressed");
      return undefined;
    }
    if (!selectedTenant || !baseUrl.trim()) {
      setStatus("idle");
      return undefined;
    }

    const timer = setTimeout(async () => {
      if (generation !== generationRef.current) return;
      setStatus("loading");
      const controller = new AbortController();
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, REQUEST_TIMEOUT_MILLISECONDS);
      activeRequestRef.current = { controller, timeoutId, generation };

      try {
        const { data } = await axiosInstanceSearch.post(
          buildTypeaheadUrl(baseUrl),
          { query, limit: 5 },
          { headers: { TenantId: selectedTenant }, signal: controller.signal }
        );
        clearTimeout(timeoutId);
        if (generation !== generationRef.current) {
          return;
        }
        activeRequestRef.current = null;
        const nextProducts = asArray(readValue(data, "products", "Products"));
        const nextCategories = asArray(readValue(data, "categories", "Categories"));
        setResponse(data);
        setStatus(readValue(data, "suppressed", "Suppressed") ? "suppressed" : nextProducts.length || nextCategories.length ? "success" : "empty");
      } catch (requestError) {
        clearTimeout(timeoutId);
        if (generation !== generationRef.current) return;
        activeRequestRef.current = null;
        if (!timedOut && (requestError?.code === "ERR_CANCELED" || requestError?.name === "AbortError")) return;
        setError(timedOut ? "The typeahead request timed out. Try again." : getErrorMessage(requestError));
        setStatus("error");

      }
    }, DEBOUNCE_MILLISECONDS);

    return () => {
      clearTimeout(timer);
      if (activeRequestRef.current?.generation === generation) activeRequestRef.current.controller.abort();
    };
  }, [baseUrl, enabled, eligible, query, retryKey, selectedTenant]);

  useEffect(() => () => {
    generationRef.current += 1;
    activeRequestRef.current?.controller.abort();
    if (activeRequestRef.current) clearTimeout(activeRequestRef.current.timeoutId);
  }, []);

  const activateSuggestion = (index) => {
    if (index < categories.length) {
      const value = readValue(categories[index], "value", "Value");
      if (!value) return;
      cancelCurrentInteraction();
      setActiveIndex(-1);
      setStatus("idle");
      setQuery(value);
      setRetryKey((current) => current + 1);
      inputRef.current?.focus();
      return;
    }
    const product = products[index - categories.length];
    const link = readValue(product, "productLink", "ProductLink");
    if (link) window.open(link, "_blank", "noopener,noreferrer");
  };

  const moveActive = (nextIndex) => {
    if (!suggestionCount) return;
    setActiveIndex((nextIndex + suggestionCount) % suggestionCount);
  };

  const cancelCurrentInteraction = () => {
    generationRef.current += 1;
    if (activeRequestRef.current) {
      clearTimeout(activeRequestRef.current.timeoutId);
      activeRequestRef.current.controller.abort();
      activeRequestRef.current = null;
    }
  };

  const submitQuery = () => {
    if (!query.trim()) return;
    cancelCurrentInteraction();
    setStatus("idle");
    setActiveIndex(-1);
    onSubmitQuery?.(query.trim());
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown" && suggestionCount) {
      event.preventDefault();
      moveActive(activeIndex + 1);
    } else if (event.key === "ArrowUp" && suggestionCount) {
      event.preventDefault();
      moveActive(activeIndex <= 0 ? suggestionCount - 1 : activeIndex - 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (activeIndex >= 0) activateSuggestion(activeIndex);
      else submitQuery();
    } else if (event.key === "Escape") {
      cancelCurrentInteraction();
      setActiveIndex(-1);
      setStatus("idle");
    }
  };

  const queryField = <>
            <Box sx={{ position: "relative" }}>
              <TextField
                inputRef={inputRef}
                fullWidth
                label="Raw shopper query"
                sx={inputSx}
                onBlur={() => { cancelCurrentInteraction(); setStatus("idle"); setActiveIndex(-1); }}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={handleKeyDown}
                inputProps={{
                  maxLength: 256,
                  role: enabled ? "combobox" : undefined,
                  "aria-autocomplete": "list",
                  "aria-expanded": popupOpen,
                  "aria-controls": SUGGESTION_LISTBOX_ID,
                  "aria-activedescendant": activeOptionId,
                  autoComplete: "off",
                  spellCheck: false,
                }}
                helperText={`${query.length} / 256 · ${enabled ? "Typeahead suggests categories and products for queries up to 128 characters." : "Try “drills under $200”, “queen size mattress”, or “new arrivals”. Size keywords and catalogue values depend on tenant settings."}`}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ color: "#f97316" }} /></InputAdornment> }}
              />
              {popupOpen ? (
                <Paper elevation={8} sx={{ position: "absolute", zIndex: 20, top: "calc(100% - 18px)", left: 0, right: 0, maxHeight: 430, overflowY: "auto", borderRadius: "0 0 16px 16px", border: "1px solid rgba(15,23,42,0.12)" }}>
                  {status === "loading" ? <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}><CircularProgress size={18} sx={{ color: "#f97316" }} /><Typography variant="body2">Loading categories and products…</Typography></Stack> : null}
                  {status === "empty" ? <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No matching categories or products.</Typography> : null}
                  {status === "error" ? <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ p: 1.5 }}><Typography variant="body2" color="error.main">{error}</Typography><Button startIcon={<ReplayOutlined />} onMouseDown={(event) => event.preventDefault()} onClick={() => setRetryKey((value) => value + 1)} sx={{ textTransform: "none" }}>Retry</Button></Stack> : null}
                  <Box id={SUGGESTION_LISTBOX_ID} role="listbox" aria-label="Categories and products">
                    {categories.length ? <Typography role="presentation" variant="overline" sx={{ display: "block", px: 1.5, pt: 1, color: "#64748b", fontWeight: 850 }}>Categories</Typography> : null}
                    {categories.map((category, index) => (
                      <Box key={`${readValue(category, "value", "Value")}-${index}`}
                        id={`typeahead-option-${generationRef.current}-${index}`} role="option"
                        aria-selected={activeIndex === index} onMouseEnter={() => setActiveIndex(index)}
                        onMouseDown={(event) => event.preventDefault()} onClick={() => activateSuggestion(index)}
                        sx={{ display: "flex", gap: 1.5, alignItems: "center", p: 1.5, cursor: "pointer", borderTop: "1px solid rgba(15,23,42,0.06)", backgroundColor: activeIndex === index ? "rgba(249,115,22,0.09)" : "white", "&:hover": { backgroundColor: "rgba(249,115,22,0.07)" } }}>
                        <CategoryOutlined sx={{ color: "#f97316" }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>{readValue(category, "label", "Label")}</Typography>
                          <Typography variant="caption" color="text.secondary">Category · {Number(readValue(category, "productCount", "ProductCount") || 0).toLocaleString()} products</Typography>
                        </Box>
                      </Box>
                    ))}
                    {products.length ? <Typography role="presentation" variant="overline" sx={{ display: "block", px: 1.5, pt: 1, color: "#64748b", fontWeight: 850 }}>Products</Typography> : null}
                    {products.map((product, productIndex) => {
                        const index = categories.length + productIndex;
                        const id = readValue(product, "productId", "ProductId");
                        const title = readValue(product, "title", "Title") || readValue(product, "completionText", "CompletionText") || "Untitled product";
                        const image = readValue(product, "primaryImageUrl", "PrimaryImageUrl");
                        const vendor = readValue(product, "vendor", "Vendor");
                        const productType = readValue(product, "productType", "ProductType");
                        const matchKind = readValue(product, "matchKind", "MatchKind") || "prefix";
                        const price = formatPrice(readValue(product, "minimumPrice", "MinimumPrice"));
                        return (
                          <Box
                            key={`${id}-${index}`}
                            id={`typeahead-option-${generationRef.current}-${index}`}
                            role="option"
                            aria-selected={activeIndex === index}
                            onMouseEnter={() => setActiveIndex(index)}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => activateSuggestion(index)}
                            sx={{ display: "grid", gridTemplateColumns: "52px minmax(0,1fr) auto", gap: 1.1, alignItems: "center", p: 1.15, cursor: "pointer", borderTop: "1px solid rgba(15,23,42,0.06)", backgroundColor: activeIndex === index ? "rgba(249,115,22,0.09)" : "white", "&:hover": { backgroundColor: "rgba(249,115,22,0.07)" } }}
                          >
                            <Box sx={{ position: "relative", width: 52, height: 52, display: "grid", placeItems: "center", overflow: "hidden", borderRadius: "10px", backgroundColor: "#f8fafc", color: "#cbd5e1" }}>
                              <Inventory2Outlined />
                              {image ? <Box component="img" src={image} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", backgroundColor: "#f8fafc" }} /> : null}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis" }}>{title}</Typography>
                              <Typography variant="caption" color="text.secondary">{[vendor, productType].filter(Boolean).join(" · ") || id}</Typography>
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.35 }}>
                                <Chip size="small" label={matchKind} color={matchKindColor(matchKind)} variant="outlined" />
                                <Typography variant="caption" color="text.secondary">{readValue(product, "displayVariantCount", "DisplayVariantCount") ?? 0} variants · {Number(readValue(product, "displayStockCount", "DisplayStockCount") || 0).toLocaleString()} stock</Typography>
                              </Stack>
                            </Box>
                            <Stack alignItems="flex-end" spacing={0.3}>
                              <Typography variant="body2" sx={{ fontWeight: 850 }}>{price || "—"}</Typography>
                              <LaunchOutlined sx={{ fontSize: 16, color: "#94a3b8" }} />
                            </Stack>
                          </Box>
                        );
                    })}
                  </Box>
                </Paper>
              ) : null}
            </Box>
            <Box aria-live="polite" aria-atomic="true" sx={{ position: "absolute", width: 1, height: 1, p: 0, m: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>
              {status === "loading" ? "Loading categories and products." : status === "empty" ? "No suggestions available." : status === "error" ? error : suggestionCount ? `${categories.length} category suggestions and ${products.length} product completions available.` : ""}
            </Box>
  </>;

  return queryField;
};

export { buildTypeaheadUrl, normalizeTypeaheadInput, searchableCharacterCount };
export default SearchQueryInput;
